import { useCallback, useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';

import { useAuthContext, useTodoTasksContext } from '../context';
import {
  SupportedTodoProvider,
  useSubscribeTodoWatch,
  useSyncAppleReminders,
  useSyncTodoProviders,
} from '../mutations';
import { isSyncingTodosAtom } from '../recoil';
import { TodoIntegrationType } from '@demind-inc/core';
import {
  fetchAppleRemindersFromDesktop,
  isAppleRemindersDesktopSupported,
  sanitizeRemindersForSync,
  loadPreviousSyncState,
  saveSyncState,
  computeDeletedReminderIds,
} from '../../helpers/appleRemindersDesktop.helpers';

export const useSyncTodosApi = () => {
  const { user } = useAuthContext();
  const { todoProjects } = useTodoTasksContext();
  const { syncProviderTasks, isPending: isSyncingTodos } = useSyncTodoProviders();
  const { subscribeTodoWatch } = useSubscribeTodoWatch();
  const { syncAppleReminders, isPending: isSyncingAppleReminders } = useSyncAppleReminders();
  const [, setIsSyncingTodos] = useRecoilState(isSyncingTodosAtom);
  const hasSynced = useRef(false);
  const hasSubscribed = useRef(false);

  // Helper function to group calendar IDs by provider type
  const groupBoardsByProvider = useCallback(
    (boardIds: string[]) => {
      const todoistBoards: string[] = [];
      const githubBoards: string[] = [];
      const ticktickBoards: string[] = [];
      const trelloBoards: string[] = [];
      const notionBoards: string[] = [];
      const clickupBoards: string[] = [];
      const googleTasksBoards: string[] = [];
      const appleRemindersBoards: string[] = [];

      boardIds.forEach((boardId) => {
        const board = todoProjects.find((project) => project.todoTaskId === boardId);
        if (board) {
          if (board.appFrom === 'todoist') {
            todoistBoards.push(boardId);
          } else if (board.appFrom === 'github') {
            githubBoards.push(boardId);
          } else if (board.appFrom === 'ticktick') {
            ticktickBoards.push(boardId);
          } else if (board.appFrom === 'trello') {
            trelloBoards.push(boardId);
          } else if (board.appFrom === 'notion') {
            notionBoards.push(boardId);
          } else if (board.appFrom === 'clickup') {
            clickupBoards.push(boardId);
          } else if (board.appFrom === 'google-tasks') {
            googleTasksBoards.push(boardId);
          } else if (board.appFrom === 'apple-reminders') {
            appleRemindersBoards.push(board.originalBoardId);
          }
        }
      });

      return {
        todoistBoards,
        githubBoards,
        ticktickBoards,
        trelloBoards,
        notionBoards,
        clickupBoards,
        googleTasksBoards,
        appleRemindersBoards,
      };
    },
    [todoProjects]
  );

  useEffect(() => {
    setIsSyncingTodos(isSyncingTodos || isSyncingAppleReminders);
  }, [isSyncingTodos, isSyncingAppleReminders]);

  const subscribeToTodoWatches = useCallback(
    async (todoTasksBoardIds: string[] = user.todoTasksBoardIds ?? []) => {
      if (!user.userId || hasSubscribed.current) {
        return;
      }

      const { clickupBoards, githubBoards, trelloBoards } = groupBoardsByProvider(todoTasksBoardIds);

      const promises = [];

      if (trelloBoards.length > 0) {
        promises.push(
          subscribeTodoWatch({
            todoTasksBoardIds: trelloBoards,
            provider: 'trello',
            userId: user.userId,
          })
        );
      }

      if (githubBoards.length > 0) {
        promises.push(
          subscribeTodoWatch({
            todoTasksBoardIds: githubBoards,
            provider: 'github',
            userId: user.userId,
          })
        );
      }

      if (clickupBoards.length > 0) {
        promises.push(
          subscribeTodoWatch({
            todoTasksBoardIds: clickupBoards,
            provider: 'clickup',
            userId: user.userId,
          })
        );
      }

      if (promises.length > 0) {
        hasSubscribed.current = true;
        await Promise.all(promises);
      }
    },
    [subscribeTodoWatch, groupBoardsByProvider, user.todoTasksBoardIds]
  );

  const syncAllTodoTasks = useCallback(
    async (
      todoTasksBoardIds: string[] = user.todoTasksBoardIds ?? [],
      fullSync: boolean = false,
      skipFlag?: boolean
    ) => {
      if (!user.userId || (hasSynced.current && !skipFlag)) {
        return;
      }

      const {
        todoistBoards,
        githubBoards,
        ticktickBoards,
        trelloBoards,
        notionBoards,
        clickupBoards,
        googleTasksBoards,
        appleRemindersBoards,
      } = groupBoardsByProvider(todoTasksBoardIds);
      const promises = [];

      if (todoistBoards.length > 0) {
        promises.push(
          syncProviderTasks('todoist', {
            userId: user.userId,
            todoTasksBoardIds: todoistBoards,
            fullSync: fullSync,
          })
        );
      }

      if (githubBoards.length > 0) {
        promises.push(
          syncProviderTasks('github', {
            userId: user.userId,
            todoTasksBoardIds: githubBoards,
            fullSync: fullSync,
          })
        );
      }

      if (ticktickBoards.length > 0) {
        promises.push(
          syncProviderTasks('ticktick', {
            userId: user.userId,
            todoTasksBoardIds: ticktickBoards,
            fullSync: fullSync,
          })
        );
      }

      if (trelloBoards.length > 0) {
        promises.push(
          syncProviderTasks('trello', {
            userId: user.userId,
            todoTasksBoardIds: trelloBoards,
            fullSync: fullSync,
          })
        );
      }

      if (notionBoards.length > 0) {
        promises.push(
          syncProviderTasks('notion', {
            userId: user.userId,
            todoTasksBoardIds: notionBoards,
            fullSync: fullSync,
          })
        );
      }

      if (clickupBoards.length > 0) {
        promises.push(
          syncProviderTasks('clickup', {
            userId: user.userId,
            todoTasksBoardIds: clickupBoards,
            fullSync: fullSync,
          })
        );
      }

      if (googleTasksBoards.length > 0) {
        promises.push(
          syncProviderTasks('google-tasks', {
            userId: user.userId,
            todoTasksBoardIds: googleTasksBoards,
            fullSync: fullSync,
          })
        );
      }

      if (appleRemindersBoards.length > 0 && isAppleRemindersDesktopSupported()) {
        promises.push(
          fetchAppleRemindersFromDesktop()
            .then(({ lists, reminders }) => {
              const allowedLists = lists.filter((l) => appleRemindersBoards.includes(l.id));
              const allowedListIds = allowedLists.map((l) => l.id);
              const allowedListIdSet = new Set(allowedListIds);
              const allowedReminders = reminders.filter((r) => allowedListIdSet.has(r.calendar?.id));

              const previousState = loadPreviousSyncState(user.userId);
              const currentReminderIds = allowedReminders.map((r) => r.id);
              const deletedReminderIds = computeDeletedReminderIds(previousState, currentReminderIds, allowedListIds);

              saveSyncState(user.userId, {
                reminderIds: currentReminderIds,
                allowedListIds,
                lastSyncTime: new Date().toISOString(),
              });

              return syncAppleReminders({
                userId: user.userId,
                metricId: user.metricId,
                userPersona: user.persona?.job,
                lists: allowedLists,
                reminders: sanitizeRemindersForSync(allowedReminders),
                deletedReminderIds: deletedReminderIds.length > 0 ? deletedReminderIds : undefined,
              });
            })
            .catch(() => void 0)
        );
      }

      if (promises.length > 0) {
        hasSynced.current = true;
        await Promise.all(promises);
      }
    },
    [groupBoardsByProvider, user.userId, user.todoTasksBoardIds]
  );

  const reSyncTasks = useCallback(
    async (
      provider: TodoIntegrationType | 'all',
      todoTasksBoardIds?: string[],
      fullSync?: boolean
    ) => {
      if (!user.userId) {
        return;
      }
      if (provider === 'all') {
        await syncAllTodoTasks(todoTasksBoardIds, fullSync, true);
        return;
      }

      if (provider === 'apple-reminders' && isAppleRemindersDesktopSupported()) {
        const { lists, reminders } = await fetchAppleRemindersFromDesktop();

        const appleOriginalBoardIds = todoProjects
          .filter((p) => p.appFrom === 'apple-reminders')
          .map((p) => p.originalBoardId);
        const allowedLists = appleOriginalBoardIds.length > 0
          ? lists.filter((l) => appleOriginalBoardIds.includes(l.id))
          : lists;
        const allowedListIds = allowedLists.map((l) => l.id);
        const allowedListIdSet = new Set(allowedListIds);
        const allowedReminders = reminders.filter((r) => allowedListIdSet.has(r.calendar?.id));

        const previousState = loadPreviousSyncState(user.userId);
        const currentReminderIds = allowedReminders.map((r) => r.id);
        const deletedReminderIds = computeDeletedReminderIds(previousState, currentReminderIds, allowedListIds);

        saveSyncState(user.userId, {
          reminderIds: currentReminderIds,
          allowedListIds,
          lastSyncTime: new Date().toISOString(),
        });

        await syncAppleReminders({
          userId: user.userId,
          metricId: user.metricId,
          userPersona: user.persona?.job,
          lists: allowedLists,
          reminders: sanitizeRemindersForSync(allowedReminders),
          deletedReminderIds: deletedReminderIds.length > 0 ? deletedReminderIds : undefined,
        });
        return;
      }

      await syncProviderTasks(provider as SupportedTodoProvider, {
        userId: user.userId,
        todoTasksBoardIds: todoTasksBoardIds,
        fullSync: fullSync,
      });
    },
    [syncAllTodoTasks, user.userId, syncAppleReminders, user.metricId, user.persona?.job]
  );

  return { syncAllTodoTasks, reSyncTasks, subscribeToTodoWatches, isSyncingTodos };
};
